import React, { Component } from "react";

// Import Data
import species from "./data/algae_prices.json";
import CHARS from "./data/algae_chars.json";
import PRODUCTS from "./data/Products.json";
import defaultConfig from "./data/settings.json";

import AboutHTML from "./AboutHTML";
import Process from "./Process";
import DefaultSettings from "./DefaultSettings";
import PopoverDiv from "./Popover";
import TotalCapitalRequirement from "./TotalCapitalRequirement";

import PieChartCard from "./PieChartCard";

export default class StepForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      step: 1,
      totalSteps: 3,

      // Step 1
      selectedSpecie: null,
      price: "",
      capacity: "",
      priceError: false,
      capacityError: false,
      showValidationMessage: false,

      chars: CHARS,
      about: false,

      // Each element: { numProducts, products[], yields{}, feedSource, currentCapacity, capex, installed, opex, meta{} }
      processSteps: [{}],

      // Settings
      settings: JSON.parse(JSON.stringify(defaultConfig)),
      defaultSettings: JSON.parse(JSON.stringify(defaultConfig)),
      showSettings: false,
    };

    this.handleCapexUpdate = this.handleCapexUpdate.bind(this);
    this.handleProcessMetadata = this.handleProcessMetadata.bind(this);
  }

  // ---------------------------------------------------------
  // STEP 1 VALIDATION
  // ---------------------------------------------------------
  validatePrice = (e) => {
    const value = e.target.value;
    const num = Number(value);
    const error = !(value !== "" && !isNaN(num) && num > 0);
    this.setState({
      price: value,
      priceError: error,
    });
  };

  checkCapacity = (e) => {
    const value = e.target.value;
    const num = Number(value);
    const error = !(value !== "" && !isNaN(num) && num > 0);

    this.setState(
      {
        capacity: value,
        capacityError: error,
      },
      this.recalculateMassFlow
    );
  };

  isStep1Valid = () => {
    const { selectedSpecie, priceError, capacityError, price, capacity } =
      this.state;

    return (
      selectedSpecie &&
      price !== "" &&
      !priceError &&
      capacity !== "" &&
      !capacityError
    );
  };

  // ---------------------------------------------------------
  // NAVIGATION
  // ---------------------------------------------------------
  nextStep = () => {
    const { step, totalSteps } = this.state;

    if (step === 1 && !this.isStep1Valid()) {
      this.setState({ showValidationMessage: true });
      return;
    }
    if (step < totalSteps) {
      this.setState({ step: step + 1 });
    }
  };

  prevStep = () => {
    if (this.state.step > 1) {
      this.setState({ step: this.state.step - 1 });
    }
  };

  // ---------------------------------------------------------
  // STEP 1 LOGIC
  // ---------------------------------------------------------
  updatePrice = (e) => {
    if (e.target.value === "-1") {
      this.setState({
        price: "",
        selectedSpecie: null,
      });
    } else {
      const item = species.filter((a) => a.species === e.target.value)[0];
      this.setState({
        price: item.cost,
        selectedSpecie: e.target.value.toLowerCase(),
      });
    }
  };

  setAbout = () => {
    this.setState((prev) => ({ about: !prev.about }));
  };

  // ---------------------------------------------------------
  // PROCESS MANAGEMENT (STEP 2)
  // ---------------------------------------------------------
  addProcessStep = () => {
    this.setState(
      (prev) => ({
        processSteps: [...prev.processSteps, {}],
      }),
      this.recalculateMassFlow
    );
  };

  removeProcess = (index) => {
    this.setState(
      (prev) => {
        const updated = prev.processSteps.filter((_, i) => i !== index);
        return { processSteps: updated.length ? updated : [{}] };
      },
      this.recalculateMassFlow
    );
  };

  handleProcessChange = (index, data) => {
    this.setState(
      (prev) => {
        const arr = [...prev.processSteps];
        arr[index] = { ...arr[index], ...data };
        return { processSteps: arr };
      },
      this.recalculateMassFlow
    );
  };

  handleFeedChange = (index, feedName) => {
    this.setState(
      (prev) => {
        const arr = [...prev.processSteps];
        arr[index] = { ...arr[index], feedSource: feedName };
        return { processSteps: arr };
      },
      this.recalculateMassFlow
    );
  };

  handleProductYieldChange = (processIndex, productName, yld) => {
    this.setState(
      (prev) => {
        const arr = [...prev.processSteps];
        const proc = { ...(arr[processIndex] || {}) };
        const yields = { ...(proc.yields || {}) };

        yields[productName] = Number(yld) || 0;
        proc.yields = yields;
        arr[processIndex] = proc;

        return { processSteps: arr };
      },
      this.recalculateMassFlow
    );
  };

  handleCapexUpdate(index, data) {
    this.setState((prev) => {
      const arr = [...prev.processSteps];
      arr[index] = { ...arr[index], ...data };
      return { processSteps: arr };
    });
  }

  handleProcessMetadata(index, meta) {
    this.setState((prev) => {
      const arr = [...prev.processSteps];
      const old = arr[index] || {};
      arr[index] = { ...old, meta: { ...meta } };
      return { processSteps: arr };
    });
  }

  // ---------------------------------------------------------
  // MASS FLOW
  // ---------------------------------------------------------
  recalculateMassFlow = () => {
    this.setState((prev) => {
      const processSteps = [...prev.processSteps];
      const baseCapacity = Number(prev.capacity) || 0;

      if (!processSteps.length) return { processSteps };

      // First process
      processSteps[0] = {
        ...(processSteps[0] || {}),
        currentCapacity: baseCapacity,
      };

      // Downstream
      for (let i = 1; i < processSteps.length; i++) {
        const prevProc = processSteps[i - 1] || {};
        const proc = { ...(processSteps[i] || {}) };

        const prevCap =
          typeof prevProc.currentCapacity === "number"
            ? prevProc.currentCapacity
            : baseCapacity;

        const prevProducts = prevProc.products || [];
        const yields = prevProc.yields || {};
        const numProducts =
          typeof prevProc.numProducts === "number"
            ? prevProc.numProducts
            : prevProducts.length;

        let y = 1;

        if (prevProducts.length === 0) {
          y = 1;
        } else if (numProducts <= 1) {
          const only = prevProducts[0];
          if (only && typeof yields[only] === "number") y = yields[only];
        } else {
          const feedName =
            proc.feedSource || (prevProducts.length ? prevProducts[0] : null);
          if (feedName && typeof yields[feedName] === "number")
            y = yields[feedName];
        }

        proc.currentCapacity = prevCap * y;
        processSteps[i] = proc;
      }

      return { processSteps };
    });
  };

  // ---------------------------------------------------------
  // SETTINGS
  // ---------------------------------------------------------
  updateSetting = (section, key, value) => {
    const parsedValue = value === "" ? "" : Number(value);
    this.setState((prev) => {
      const settings = { ...prev.settings };
      if (section) {
        settings[section] = { ...settings[section], [key]: parsedValue };
      } else {
        settings[key] = parsedValue;
      }
      return { settings };
    });
  };

  resetSettings = () => {
    this.setState({
      settings: JSON.parse(JSON.stringify(defaultConfig)),
    });
  };

  saveSettingsAndClose = () => {
    this.setState({ showSettings: false });
  };

  handleFinalProductChange = (processIndex, productName, isFinal) => {
  this.setState((prev) => {
    const steps = [...prev.processSteps];
    const p = steps[processIndex] || {};
    const finals = { ...(p.finalProducts || {}) };

    finals[productName] = {
      ...(finals[productName] || {}),
      isFinal,
    };

    steps[processIndex] = { ...p, finalProducts: finals };
    return { processSteps: steps };
  });
};

handleProductPriceChange = (processIndex, productName, price) => {
  this.setState((prev) => {
    const steps = [...prev.processSteps];
    const p = steps[processIndex] || {};
    const finals = { ...(p.finalProducts || {}) };

    finals[productName] = {
      ...(finals[productName] || {}),
      price: Number(price),
    };

    steps[processIndex] = { ...p, finalProducts: finals };
    return { processSteps: steps };
  });
};


  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------
  render() {
    const {
      about,
      price,
      capacity,
      priceError,
      capacityError,
      step,
      selectedSpecie,
      chars,
      showSettings,
      settings,
      processSteps,
      showValidationMessage,
    } = this.state;

    const nextDisabled =
      step === 1 ? !this.isStep1Valid() : step === this.state.totalSteps;

    const operatingHoursPerYear = settings.operatingHoursPerYear || 1;
    const baseCapacity = Number(capacity) || 0;

    // Subtotals for summary
    let subtotalBare = 0;
    let subtotalInstalled = 0;
    processSteps.forEach((p) => {
      if (typeof p.capex === "number") subtotalBare += p.capex;
      if (typeof p.installed === "number") subtotalInstalled += p.installed;
    });

    

    return (
      <div className="min-h-screen w-full flex flex-col items-center p-6 gap-6">
        {/* HEADER */}
        <header className="w-full flex justify-between items-center p-4 border-b">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-left">ALGAE ECONOMIST</h1>
            <h1 className="text-l">
              Algae Economy Screening and Optimization Tool
            </h1>
          </div>

          <button
            onClick={this.setAbout}
            className="px-4 py-2 flex gap-2 bg-white hover:bg-gray-200 rounded-lg border shadow"
          >
            <span>About</span>
          </button>
        </header>

        {/* ABOUT */}
        {about ? (
          <AboutHTML />
        ) : (
          <div className="max-w-3xl w-full p-6 rounded-2xl shadow-md border">
            {/* STEP 1 */}
            <section
              className={
                "flex flex-col gap-4" + (step === 1 ? "" : " hidden")
              }
            >
              <h3 className="font-semibold text-lg">
                Step 1: Select Cultivation System
              </h3>

              {/* SPECIES */}
              <div>
                <label className="font-medium" htmlFor="algae_species">
                  Algae species (<i>Cultivation System</i>)
                </label>
                <div className="flex">
                  <select
                    id="algae_species"
                    onChange={this.updatePrice}
                    className="w-full border p-1 rounded"
                  >
                    <option value={-1}>...</option>
                    {species
                      .sort((a, b) => a.species.localeCompare(b.species))
                      .map((s) => (
                        <option
                          key={s.species + s.cultivation}
                          value={s.species}
                        >
                          {s.species} ({s.cultivation})
                        </option>
                      ))}
                  </select>

                  {selectedSpecie && (
                    <PopoverDiv specie={chars[selectedSpecie]} />
                  )}
                </div>
              </div>

              {/* PRICE */}
              <div>
                <label className="font-medium">Price</label>
                <input
                  type="text"
                  className={
                    "w-full border p-1 rounded " +
                    (priceError ? "border-red-600 bg-red-50" : "")
                  }
                  value={price}
                  onChange={this.validatePrice}
                />
                <span> EUR/ton</span>
                {priceError && (
                  <div className="text-red-600 text-sm mt-1">
                    Invalid price.
                  </div>
                )}
              </div>

              {/* CAPACITY */}
              <div>
                <label className="font-medium">Capacity</label>
                <input
                  type="text"
                  className={
                    "w-full border p-1 rounded bg-yellow-100 " +
                    (capacityError ? "border-red-600" : "")
                  }
                  value={capacity}
                  onChange={this.checkCapacity}
                />
                <span className="text-red-800">*</span> ton/year
                {capacityError && (
                  <div className="text-red-600 text-sm mt-1">
                    Invalid capacity.
                  </div>
                )}
              </div>
            </section>

            {/* STEP 2 */}
            <section
              className={
                "flex flex-col gap-4" + (step === 2 ? "" : " hidden")
              }
            >
              <h3 className="font-semibold text-lg">
                Step 2: Processing Steps ({processSteps.length})
              </h3>

              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-green-800 mb-2">
                  Cultivation Summary
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Species</div>
                    <div className="font-medium text-gray-800">
                      {selectedSpecie
                        ? chars[selectedSpecie].specie
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Price</div>
                    <div className="font-medium text-gray-800">
                      {price ? `${price} EUR/kg` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Capacity</div>
                    <div className="font-medium text-gray-800">
                      {capacity ? `${capacity} ton/year` : "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Add Step */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">
                      Processing Steps ({processSteps.length})
                    </h4>
                    <p className="text-gray-500 text-sm">
                      Add unit operations to your biorefinery pipeline.
                    </p>
                  </div>
                  <button
                    onClick={this.addProcessStep}
                    className="px-4 py-2 bg-green-700 text-white rounded-lg shadow hover:bg-green-800 transition"
                  >
                    + Add Step
                  </button>
                </div>
                <hr />
              </div>

              {/* Process List */}
              {processSteps.map((proc, i) => {
                const processId = i + 1;
                const prevProcess = i > 0 ? processSteps[i - 1] : null;
                const prevNumProducts =
                  prevProcess && typeof prevProcess.numProducts === "number"
                    ? prevProcess.numProducts
                    : prevProcess && prevProcess.products
                    ? prevProcess.products.length
                    : null;

                const showFeedSelector =
                  i > 0 && prevNumProducts && prevNumProducts > 1;

                const feedOptions =
                  showFeedSelector &&
                  prevProcess &&
                  prevProcess.products
                    ? prevProcess.products
                    : [];

                const currentCapacity =
                  typeof proc.currentCapacity === "number"
                    ? proc.currentCapacity
                    : baseCapacity;

                return (
                  <Process
                    key={"process_" + processId}
                    processIndex={i}
                    processId={processId}
                    productsList={PRODUCTS}
                    feedOptions={feedOptions}
                    showFeedSelector={showFeedSelector}
                    isLast={i === processSteps.length - 1}
                    currentCapacity={currentCapacity}
                    operatingHoursPerYear={operatingHoursPerYear}
                    onDelete={() => this.removeProcess(i)}
                    onProcessChange={this.handleProcessChange}
                    onFeedChange={this.handleFeedChange}
                    onProductYieldChange={this.handleProductYieldChange}
                    onCapexUpdate={this.handleCapexUpdate}
                    onProcessMetadata={this.handleProcessMetadata}
                    onFinalProductChange={this.handleFinalProductChange}
                    onProductPriceChange={this.handleProductPriceChange}
                  />
                );
              })}
            </section>

            {/* STEP 3 — SUMMARY TABLE */}
            <section
              className={
                "flex flex-col gap-4" + (step === 3 ? "" : " hidden")
              }
            >
              <h3 className="font-semibold text-lg">
                Step 3: Processing Summary
              </h3>

              <div className="overflow-x-auto hidden">
                <table className="min-w-full border text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-2 py-1">Process</th>
                      <th className="border px-2 py-1">
                        Current Capacity [ton/h]
                      </th>
                      <th className="border px-2 py-1">
                        Reference Capacity RC [ton/h]
                      </th>
                      <th className="border px-2 py-1">
                        Reference Investment I [EUR]
                      </th>
                      <th className="border px-2 py-1">F</th>
                      <th className="border px-2 py-1">n</th>
                      <th className="border px-2 py-1">LF</th>
                      <th className="border px-2 py-1">
                        Bare equipment costs [EUR]
                      </th>
                      <th className="border px-2 py-1">
                        Installed equipment costs [EUR]
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {processSteps.map((p, i) => {
                      const m = p.meta || {};
                      const tonHour =
                        (p.currentCapacity || 0) /
                        (operatingHoursPerYear || 1);

                      const bare = typeof p.capex === "number" ? p.capex : null;
                      const inst =
                        typeof p.installed === "number"
                          ? p.installed
                          : null;

                      const I =
                        m.invesment && !isNaN(m.invesment)
                          ? m.invesment * 1000
                          : null;

                      return (
                        <tr key={i} className="text-center">
                          <td className="border px-2 py-1">{m.step || "—"}</td>
                          <td className="border px-2 py-1">
                            {tonHour ? tonHour.toFixed(3) : "—"}
                          </td>
                          <td className="border px-2 py-1">
                            {m.capacity || "—"}
                          </td>
                          <td className="border px-2 py-1">
                            {I ? I.toLocaleString() : "—"}
                          </td>
                          <td className="border px-2 py-1">
                            {m.conversion_factor ?? "—"}
                          </td>
                          <td className="border px-2 py-1">{m.n ?? "—"}</td>
                          <td className="border px-2 py-1">
                            {m.langfactor ?? "—"}
                          </td>
                          <td className="border px-2 py-1">
                            {bare ? bare.toLocaleString() : "—"}
                          </td>
                          <td className="border px-2 py-1">
                            {inst ? inst.toLocaleString() : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-semibold">
                      <td
                        className="border px-2 py-1 text-right"
                        colSpan={7}
                      >
                        Subtotal
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {subtotalBare
                          ? subtotalBare.toLocaleString()
                          : "—"}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {subtotalInstalled
                          ? subtotalInstalled.toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                    <tr className="bg-gray-100 font-semibold">
                      <td
                        className="border px-2 py-1 text-right"
                        colSpan={7}
                      >
                        <b>Process Contingency</b> <br></br>	Concept with bench-scale data (30-70%)	

                      </td>
                      <td className="border px-2 py-1 text-center">
                        30%
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {subtotalInstalled
                          ? (0.3 * subtotalInstalled).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                    <tr className="bg-gray-100 font-semibold">
                      <td
                        className="border px-2 py-1 text-right"
                        colSpan={7}
                      >
                        Total process Capital	

                      </td>
                      <td className="border px-2 py-1 text-center">
                        
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {subtotalInstalled
                          ? (1.3 * subtotalInstalled).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div>

              </div>


              {/* FINAL PRODUCTS & REVENUE */}
              <div className="mt-8 hidden">
                <h4 className="font-semibold text-lg mb-3">
                  Final Products & Revenue Summary
                </h4>

                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-2 py-1">Process</th>
                      <th className="border px-2 py-1">Product</th>
                      <th className="border px-2 py-1">Final Mass [ton/year]</th>
                      <th className="border px-2 py-1">Price [EUR/ton]</th>
                      <th className="border px-2 py-1">Revenue [EUR/year]</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(() => {
                      let totalMass = 0;
                      let totalRevenue = 0;

                      const rows = processSteps.flatMap((p, i) => {
                        if (!p.finalProducts || !p.yields) return [];

                        return Object.entries(p.finalProducts)
                          .filter(([, v]) => v.isFinal)
                          .map(([product, v]) => {
                            const y = p.yields[product] || 0;
                            const mass = (p.currentCapacity || 0) * y;
                            const price = v.price || 0;
                            const revenue = mass * price;

                            totalMass += mass;
                            totalRevenue += revenue;

                            return (
                              <tr key={`${i}-${product}`} className="text-center">
                                <td className="border px-2 py-1">
                                  {p.meta?.step || `Process ${i + 1}`}
                                </td>
                                <td className="border px-2 py-1">{product}</td>
                                <td className="border px-2 py-1">
                                  {mass.toLocaleString()}
                                </td>
                                <td className="border px-2 py-1">
                                  {price.toLocaleString()}
                                </td>
                                <td className="border px-2 py-1 font-medium">
                                  {revenue.toLocaleString()}
                                </td>
                              </tr>
                            );
                          });
                      });

                      return (
                        <>
                          {rows}

                          {/* TOTAL ROW */}
                          <tr className="bg-gray-100 font-semibold text-center">
                            <td className="border px-2 py-1" colSpan={2}>
                              Total
                            </td>
                            <td className="border px-2 py-1">
                              {totalMass.toLocaleString()}
                            </td>
                            <td className="border px-2 py-1">—</td>
                            <td className="border px-2 py-1">
                              {totalRevenue.toLocaleString()}
                            </td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              
              {/* Total Capital Requirement + Total Operating Costs*/}
              <TotalCapitalRequirement 
                                      settings={settings}
                                      totalProcessCapital={1.3 * subtotalInstalled}
                                      processSteps={processSteps}
                                      price={price}
                                      capacity={capacity}
              />      

              
              <PieChartCard processSteps={processSteps} /> 
                 
      
            </section>


            {/* NAVIGATION */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={this.prevStep}
                disabled={step === 1}
                className={
                  "px-4 py-2 rounded border transition " +
                  (step === 1
                    ? "opacity-40 cursor-not-allowed bg-gray-100"
                    : "bg-white hover:bg-gray-200")
                }
              >
                Back
              </button>

              {!this.isStep1Valid() &&
                step === 1 &&
                showValidationMessage && (
                  <div className="text-red-700 text-sm mr-auto ml-4">
                    ❗ Complete all required fields.
                  </div>
                )}

              <button
                onClick={this.nextStep}
                disabled={nextDisabled || step === 3}
                className={
                  "px-4 py-2 rounded text-white transition " +
                  (step === 1 && !this.isStep1Valid()
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-900 hover:bg-green-800") + 
                    (step===3? " bg-gray-400 cursor-not-allowed"
                    : " bg-green-900 hover:bg-green-800")
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* SETTINGS */}
        <div className="max-w-3xl w-full">
          <button
            onClick={() => this.setState({ showSettings: true })}
            className="px-4 py-2 rounded-lg bg-green-900 text-white shadow hover:bg-pink-900 transition"
          >
            Change Default Economic Assumption
          </button>

          {showSettings && (
            <DefaultSettings
              settings={settings}
              defaultSettings={this.state.defaultSettings}
              onChange={this.updateSetting}
              onReset={this.resetSettings}
              onSave={this.saveSettingsAndClose}
              onClose={() => this.setState({ showSettings: false })}
            />
          )}
        </div>
      </div>
    );
  }
}
